import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import defaultConfig from '../config/defaultConfig';

// 0 - Sunday
export const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

class ProjectCalendar {
  #instance = null;

  init(instance) {
    this.#instance = instance;
  }

  addOrUpdateCalendar(projectId, calendarData = null) {

    let calendarDataClone = null;

    if (calendarData) {
      calendarDataClone = _.cloneDeep(calendarData);
    } else {
      calendarDataClone = this.#instance.ganttDataApi.getProjectConfigById(projectId)?.durationData;
    }

    calendarDataClone.showTime = this.#getWorkingHours(calendarDataClone);
    calendarDataClone.showDay = this.#getWorkingDays(calendarDataClone);

    // fix from owner gantt
    this.#instance.config.worktimes = {
      global: this.#instance.getCalendar('global').getConfig(),
    };

    this.#instance.addCalendar({
      id: +projectId,
      worktime: {
        hours: calendarDataClone.showTime,
        days: ALL_DAYS.map(dayNumber => +calendarDataClone.showDay.includes(dayNumber)), // different format for calendar
      },
    });

    this.#setWorkTime(calendarDataClone);

    // clear custom days
    const oldDates = this.#instance.getCalendar('global').getConfig().dates;

    Object.keys(oldDates).forEach(day => {
      if (!ALL_DAYS.includes(+day)) {
        delete this.#instance.getCalendar('global').getConfig().dates[day];
      }
    });

    this.#setCustomWorkTime(+projectId);
  }

  #getWorkingHours(calendarData) {
    if (calendarData.showTime?.length) {
      return calendarData.showTime;
    }

    if (calendarData.startTime !== undefined && calendarData.endTime) {
      return [calendarData.startTime, calendarData.endTime];
    }

    return defaultConfig.durationData.showTime;
  }

  #getWorkingDays(calendarData) {
    const workingDays = calendarData.showDay?.length ? calendarData.showDay : defaultConfig.durationData.showDay;

    workingDays.forEach((day, index) => {
      if (+day === 7) {
        workingDays[index] = 0;
      }
    });

    return workingDays;
  }

  #setWorkTime(calendarData) {
    const workingHours = calendarData.showTime;
    const workingDays = calendarData.showDay;
  
    ALL_DAYS.forEach(dayOfWeekNumber => {
      if (workingDays.includes(dayOfWeekNumber)) {
        this.#instance.setWorkTime({ day: dayOfWeekNumber, hours: workingHours });
      } else {
        this.#instance.setWorkTime({ day: dayOfWeekNumber, hours: false });
      }
    });
  }

  #setCustomWorkTime(projectId) {
    const calendar = this.#instance.getCalendar(projectId);
    const tasks = this.#instance.ganttDataApi.getTasksForGantt(projectId).data;
    const projectTotalEstimate = _.clone(tasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent) || {});
    const customDays = this.#instance.ganttDataApi.setAndGetCustomDaysForProject(projectId);
  
    let stepDay = {};
    let tempDate = {};
    let tempDateTo = {};
    const datesPeriod = {
      maxDate: projectTotalEstimate.end_date,
      minDate: projectTotalEstimate.start_date,
    };
    let tempEndDate = {};
  
    // set repeat days
    customDays.repeat.forEach(repeatDayData => {
      if (!repeatDayData.hours || !repeatDayData.hours.length) {
        // if weekend
        repeatDayData.hours = false;
      }
  
      switch (repeatDayData.repeat) {
      case 'week':
        stepDay = this.#createDayStartedAt00(repeatDayData.from);
  
        tempEndDate = repeatDayData.repeat_to ? this.#createDayStartedAt00(repeatDayData.repeat_to) : datesPeriod.maxDate;
        tempDateTo = this.#createDayStartedAt00(repeatDayData.to);
  
        while (stepDay.valueOf() <= tempEndDate.valueOf()) {
          this.#setWorkTimeByDate(
            calendar,
            new Date(stepDay),
            repeatDayData.to ? tempDateTo : null,
            repeatDayData.hours,
          );
  
          stepDay.setDate(stepDay.getDate() + 7);
        }
  
        break;
      case 'month':
        tempDate = this.#createDayStartedAt00(repeatDayData.from).getDate();
  
        tempDateTo = this.#createDayStartedAt00(repeatDayData.to).getDate();
        stepDay = this.#createDayStartedAt00(repeatDayData.from);
        tempEndDate = repeatDayData.repeat_to ? this.#createDayStartedAt00(repeatDayData.repeat_to) : datesPeriod.maxDate;
  
        while (stepDay.valueOf() <= tempEndDate.valueOf()) {
          this.#setWorkTimeByDate(
            calendar,
            new Date(stepDay.getFullYear(), stepDay.getMonth(), tempDate),
            repeatDayData.to ? new Date(stepDay.getFullYear(), stepDay.getMonth(), tempDate) : null,
            repeatDayData.hours,
          );
  
          stepDay.setMonth(stepDay.getMonth() + 1);
        }
        break;
      case 'year':
        tempDate = this.#createDayStartedAt00(repeatDayData.from);
        tempDateTo = this.#createDayStartedAt00(repeatDayData.to);
        stepDay = this.#createDayStartedAt00(repeatDayData.from);
  
        tempEndDate = repeatDayData.repeat_to
          ? this.#createDayStartedAt00(repeatDayData.repeat_to)
          : datesPeriod.maxDate;
  
        while (stepDay.valueOf() <= tempEndDate.valueOf()) {
          this.#setWorkTimeByDate(
            calendar,
            new Date(stepDay),
            repeatDayData.to
              ? this.#createDayStartedAt00(repeatDayData.to)
              : null,
            repeatDayData.hours,
          );
  
          stepDay.setFullYear(stepDay.getFullYear() + 1);
        }
        break;
      }
    });
  
    customDays.exception.forEach(customDayData => {
      if (!customDayData.from && !customDayData.to) {
        return false;
      }
  
      if (!customDayData.hours || !customDayData.hours.length) {
        // if weekend
        customDayData.hours = false;
      }
      this.#setWorkTimeByDate(
        calendar,
        this.#createDayStartedAt00(customDayData.from),
        customDayData.to
          ? this.#createDayStartedAt00(customDayData.to)
          : null,
        customDayData.hours,
      );
    });
  }

  #createDayStartedAt00(date) {
    return this.#instance.date.day_start(moment(date).toDate());
  }

  #setWorkTimeByDate(calendar, dayFrom, dayTo, dayHours) {
    if (!dayHours) {
      dayHours = false;
    }

    if (!dayTo) {
      // if single date
      calendar.setWorkTime({ date: dayFrom, hours: dayHours });
      this.#instance.setWorkTime({ date: dayFrom, hours: dayHours });
    } else {
      // if repeatable
      while (dayFrom.valueOf() <= dayTo.valueOf()) {
        calendar.setWorkTime({ date: dayFrom, hours: dayHours });
        this.#instance.setWorkTime({ date: dayFrom, hours: dayHours });
        dayFrom.setDate(dayFrom.getDate() + 1);
      }
    }
  }
}

export default new ProjectCalendar();
